import "./ModalShell.css";
import React, { useId } from "react";
import { Modal } from "@mui/material";
import { AlertIcon, CheckCircleIcon, CloseIcon, InfoIcon, WarningIcon } from "../Icons/Icons";

// Shared frame for redesigned modals: the `.modal` look from styles/ui.css on top of MUI's Modal,
// which keeps focus trapping, Escape and backdrop-click closing. Content goes in `children`,
// buttons (`.btn`) in `actions`.

export type ModalTone = "info" | "success" | "warning" | "error";

interface ModalShellProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Shows a coloured status icon next to the title (results, warnings, errors). */
    tone?: ModalTone;
    actions?: React.ReactNode;
    children?: React.ReactNode;
    size?: "sm" | "md" | "lg";
    /** While a request is in flight: hides the close button and ignores backdrop/Escape. */
    busy?: boolean;
}

const toneIcons: Record<ModalTone, React.ReactNode> = {
    info: <InfoIcon/>,
    success: <CheckCircleIcon/>,
    warning: <WarningIcon/>,
    error: <AlertIcon/>,
};

const ModalShell: React.FC<ModalShellProps> = ({
    open,
    onClose,
    title,
    subtitle,
    tone,
    actions,
    children,
    size = "md",
    busy = false,
}) => {
    const titleId = useId();

    return (
        <Modal
            open={open}
            onClose={busy ? undefined : onClose}
            slotProps={{ backdrop: { className: "dlg-backdrop" } }}
        >
            <div className="dlg-wrap" tabIndex={-1}>
                <div
                    className={`modal dlg dlg-${size}`}
                    role={tone === "warning" || tone === "error" ? "alertdialog" : "dialog"}
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-busy={busy || undefined}
                >
                    <div className="dlg-header">
                        {tone && <span className={`dlg-tone ${tone}`}>{toneIcons[tone]}</span>}
                        <div className="dlg-heading">
                            <h2 className="modal-title dlg-title" id={titleId}>{title}</h2>
                            {subtitle && <p className="dlg-subtitle">{subtitle}</p>}
                        </div>
                        {!busy &&
                            <button type="button" className="btn btn-ghost icon-btn dlg-close" onClick={onClose} aria-label="Close">
                                <CloseIcon/>
                            </button>}
                    </div>

                    {children && <div className="dlg-body">{children}</div>}

                    {actions && <div className="modal-actions dlg-actions">{actions}</div>}
                </div>
            </div>
        </Modal>
    );
};

/** Centered spinner with an optional line of text, for in-flight states. */
export const ModalLoading: React.FC<{ label?: React.ReactNode }> = ({ label }) => (
    <div className="dlg-loading" role="status">
        <span className="spinner" aria-hidden="true"/>
        {label && <span>{label}</span>}
    </div>
);

/** Spas a bulk action applies to: nickname plus the GUID in mono. */
export const ModalSpaList: React.FC<{ spas: any[]; label?: React.ReactNode }> = ({ spas, label }) => (
    <div className="dlg-section">
        {label && <span className="dlg-label">{label}</span>}
        {spas.length > 0 ? (
            <ul className="dlg-spa-list">
                {spas.map((spa: any) => (
                    <li key={spa?.id}>
                        {spa?.nick_name && <span className="dlg-spa-name">{spa.nick_name}</span>}
                        <span className="dlg-mono">{spa?.id}</span>
                    </li>
                ))}
            </ul>
        ) : (
            <span className="dlg-muted">No spas selected.</span>
        )}
    </div>
);

export default ModalShell;
